"use client";

import { Cancel, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Slide,
  Stack,
  styled,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { type FC, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelecter } from "../../hooks/storeHooks";
import { proofReadingPermission } from "./ProofReadingPermission";
import {
  changeActiveQuestionExplanationTranslation,
  changeActiveQuestionOption1Translation,
  changeActiveQuestionOption2Translation,
  changeActiveQuestionOption3Translation,
  changeActiveQuestionOption4Translation,
  changeActiveQuestionTranslation,
  clearActiveQuestion,
  goToNextQustion,
  setProofReadedData,
} from "../../store/slices/proofReading/questionDataArraySlice";
import { allApis } from "../../request/config";
import apiRequest from "../../request/apiRequest/apiRequest";
import ConfirmationDialog from "../ConfirmationDialog";
import { setLoader, showSnack } from "../../store/slices/constants";
import mathFunction from "../../pages/exams/mathFunction";
import { loadQuestionsDataArray } from "../../store/slices/proofReading/thunks/loadQuestionsDataArray/loadQuestionDataArray";
import { QuestionData } from "../../store/slices/proofReading/thunks/loadQuestionsDataArray/interfaces/frontend";

import { TransitionProps } from "@mui/material/transitions";
import Preview from "./Preview";
import QuestionPreviewItem from "./questionPreview/QuestionPreviewItem";

// Define interfaces for better type safety
interface OptionUpdate {
  option_id: number;
  edited_option_hindi: string;
}
export const CustomDialog = styled(Dialog)<any>(() => ({
  "& .MuiDialog-paper": {
    margin: "0",
    padding: "0px",
    backgroundColor: "#F4F5FA",
    height: "100dvh",
    overflow: "hidden",
  },
}));
const StyledRichTextDiv = styled("div")<{
  empty?: boolean;
  variant?: "question" | "option" | "solution";
  leftfix?: any;
}>(({ theme, variant = "question", leftfix }) => ({
  // minHeight: "30px",
  width: leftfix ? "calc(100% - 40.53px)" : "calc(100% - 22px)",
  minWidth: leftfix ? "calc(100% - 40.53px)" : "calc(100% - 22px)",
  bgcolor: "#f9fafb",
  borderRadius: "10px",
  border: "1px solid #e5e7eb",
  padding: leftfix ? "5px 10px 5px 28.53px" : "5px 10px",
  fontFamily: "Inter",
  height: "calc(100% - 12px)",
  fontWeight: 400,
  fontSize: "0.875rem",
  lineHeight: "1.43",
  whiteSpace: "pre-wrap",
  "&:focus": {
    outline: "none",
    borderColor: theme.palette.primary.main,
  },
  "&:empty:before": {
    content: "attr(data-placeholder)",
    color: "#aaa",
    fontStyle: "italic",
  },
  "&.tag-input": {
    minHeight: "20px",
    padding: "8px 12px",
    width: "100%",
    fontSize: "14px",
  },
  "& img": {
    ...(variant === "question" && {
      maxWidth: "100%",
      height: "auto",
      objectFit: "contain",
    }),
    ...(variant === "option" && {
      maxWidth: "100%",
      height: "auto",
      objectFit: "contain",
    }),
    ...(variant === "solution" && {
      maxWidth: "100%",
      height: "auto",
      objectFit: "contain",
    }),
  },
}));

export const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
export const RichTextInput = ({
  value,
  onChange,
  placeholder,
  className = "",
  variant = "question",
  leftfix,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  variant?: "question" | "option" | "solution";
  leftfix?: string;
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastHtmlRef = useRef<string>(value);
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const currentHtml = e.currentTarget.innerHTML;
    if (currentHtml !== lastHtmlRef.current) {
      lastHtmlRef.current = currentHtml;
      onChange(currentHtml);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;

    let content = clipboardData.getData("text/html");

    if (!content) {
      content = clipboardData.getData("text/plain");
      content = content.replace(/\n/g, "<br>");
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;

    // Set image dimensions based on variant
    const images = tempDiv.getElementsByTagName("img");
    for (const img of Array.from(images)) {
      switch (variant) {
        case "question":
          img.style.maxWidth = "600px";
          break;
        case "option":
          img.style.maxWidth = "300px";
          break;
        case "solution":
          img.style.maxWidth = "400px";
          break;
      }
      img.style.height = "auto";
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(
        document.createRange().createContextualFragment(tempDiv.innerHTML)
      );

      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editorRef.current?.appendChild(
        document.createRange().createContextualFragment(tempDiv.innerHTML)
      );
    }

    handleInput(e as any);
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "100%",
      }}
    >
      {leftfix && (
        <div
          style={{
            position: "absolute",
            left: "10px",
            top: "5px",
            pointerEvents: "none",
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "0.875rem",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          {leftfix}
        </div>
      )}
      <StyledRichTextDiv
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        leftfix={leftfix}
        data-placeholder={placeholder}
        className={className}
        empty={!value}
        variant={variant}
        suppressContentEditableWarning
      />
    </div>
  );
};
export const QuestionRawContainer: FC = () => {
  const questionData = useAppSelecter(
    (state) => state.questionsDataArraySlice.activeQuestionData
  );
  if (questionData === null) return null;
  return <QuestionRawContainerJsx key={questionData.questionId} />;
};

const QuestionRawContainerJsx: FC = () => {
  // const questionRawContainerRef = useRef(null)
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activeQuestionData = useAppSelecter(
    (state) => state.questionsDataArraySlice.activeQuestionData
  );
  const { questionsDataArray, proofReadingData } = useAppSelecter(
    (state) => state.questionsDataArraySlice
  );
  const correction = activeQuestionData?.corrections ?? null;

  const token = localStorage.getItem("npc-admin-token")
    ? JSON.parse(localStorage.getItem("npc-admin-token") || "")
    : null;
  const proofReadingPermissions = localStorage.getItem("npc-admin-permissions")
    ? JSON.parse(localStorage.getItem("npc-admin-permissions") || "")
    : null;

  // State for tab selection
  const [activeTab, setActiveTab] = useState(0);

  // State for editing different parts of the question
  const [editingStates, setEditingStates] = useState<any>({
    question: correction && correction.question,
    question_editedOn: correction && correction.question_editedOn,

    explanation: correction && correction.explanation,
    explanation_editedOn: correction && correction.explanation_editedOn,

    option1: correction && correction.option1,
    option1_editedOn: correction && correction.option1_editedOn,

    option2: correction && correction.option2,
    option2_editedOn: correction && correction.option2_editedOn,

    option3: correction && correction.option3,
    option3_editedOn: correction && correction.option3_editedOn,

    option4: correction && correction.option4,
    option4_editedOn: correction && correction.option4_editedOn,
  });
  useEffect(() => {
    mathFunction();
    let timeOut: ReturnType<typeof setTimeout>;

    timeOut = setTimeout(() => mathFunction(), 500);

    return () => clearTimeout(timeOut);
  }, [questionsDataArray, editingStates, activeQuestionData]);
  // State for edited content

  const [selectedQue, setQue] = useState<any>(null);
  const [editedContent, setEditedContent] = useState<any>({
    question: correction && correction.question ? correction.question : "",
    explanation:
      correction && correction.explanation ? correction.explanation : "",
    option1: correction && correction.option1 ? correction.option1 : "",
    option2: correction && correction.option2 ? correction.option2 : "",
    option3: correction && correction.option3 ? correction.option3 : "",
    option4: correction && correction.option4 ? correction.option4 : "",
  });

  const [modalInfo, setModalInfo] = useState<{
    rawText: string;
    visible: boolean;
    proofreadedcontent?: boolean;
  }>({
    rawText: "",
    visible: false,
  });

  if (proofReadingPermissions === null)
    return <Navigate to={"/login-page"} replace />;
  if (!activeQuestionData) return null;

  // Toggle editing state for a specific field
  const toggleEditing = (field: keyof typeof editingStates, value: boolean) => {
    setEditingStates((prev: any) => ({
      ...prev,
      [field]: value,
      [String(field) + "_editedOn"]: new Date(),
    }));

    // Initialize edited content when starting to edit
    // if (value) {
    switch (field) {
      case "question":
        setEditedContent((prev: any) => ({
          ...prev,
          question: activeQuestionData.questionHindi || "",
          question_editedOn: new Date(),
        }));
        break;
      case "explanation":
        setEditedContent((prev: any) => ({
          ...prev,
          explanation: activeQuestionData.explanationHindi || "",
          explanation_editedOn: new Date(),
        }));
        break;
      case "option1":
        setEditedContent((prev: any) => ({
          ...prev,
          option1: activeQuestionData.option1Hindi || "",
          option1_editedOn: new Date(),
        }));
        break;
      case "option2":
        setEditedContent((prev: any) => ({
          ...prev,
          option2: activeQuestionData.option2Hindi || "",
          option2_editedOn: new Date(),
        }));
        break;
      case "option3":
        setEditedContent((prev: any) => ({
          ...prev,
          option3: activeQuestionData.option3Hindi || "",
          option3_editedOn: new Date(),
        }));
        break;
      case "option4":
        setEditedContent((prev: any) => ({
          ...prev,
          option4: activeQuestionData.option4Hindi || "",
          option4_editedOn: new Date(),
        }));
        break;
    }
    // }
  };

  // Handle change for any field with proper date tracking
  const handleFieldChange = (
    field: any,
    value: string,
    dispatchAction: (payload: any) => void,
    coordinateField: string,
    coordinateDateField: string
  ) => {
    // Update the edited content state
    setEditedContent((prev: any) => ({
      ...prev,
      [field]: value,
    }));

    // Create the appropriate payload for dispatch
    const payload: any = { date: new Date().toISOString() };

    switch (field) {
      case "question":
        payload.newActiveQuestionTranslation = value;
        break;
      case "explanation":
        payload.newActiveQuestionExplanationTranslation = value;
        break;
      case "option1":
        payload.newActiveQuestionOption1Translation = value;
        break;
      case "option2":
        payload.newActiveQuestionOption2Translation = value;
        break;
      case "option3":
        payload.newActiveQuestionOption3Translation = value;
        break;
      case "option4":
        payload.newActiveQuestionOption4Translation = value;
        break;
    }

    // Dispatch the action to update the state
    // dispatch(dispatchAction(payload))
  };

  // Build the payload for API submission
  const buildQuestionUpdatePayload = (question: any) => {
    const isCurrent = activeQuestionData.questionId === question.questionId;
    const payload: any = {
      question_id: question.questionId,
      is_proofread_hi: true,
    };
    const updateData = (key: string, api: string) => {
      if (isCurrent) {
        if (editingStates[key]) {
          payload[api] = editedContent[key];
        }
      } else {
        if (question.corrections && question.corrections[key]) {
          payload[api] = question.corrections[key];
        }
      }
    };
    updateData("question", "edited_question_hindi");
    const updateOption = (key: string, id: number) => {
      if (isCurrent) {
        if (editingStates[key]) {
          payload[`option_${id}_id`] = question[`option${id}Id`];
          payload[`edited_option_${id}_hindi`] = editedContent[key];
        }
      } else {
        if (question.corrections && question.corrections[key]) {
          payload[`option_${id}_id`] = question[`option${id}Id`];
          payload[`edited_option_${id}_hindi`] = question.corrections[key];
        }
      }
    };
    const options: OptionUpdate[] = [];
    updateOption("option1", 1);
    updateOption("option2", 2);
    updateOption("option3", 3);
    updateOption("option4", 4);
    if (options.length > 0) {
      payload.options = options;
    }
    updateData("explanation", "edited_explanation_hindi");
    return payload; // Return as array since the API expects an array
  };

  const handleLoadQuestiondDataArray = async () => {
    try {
      if (token === null) return;
      await dispatch(loadQuestionsDataArray({ token })).unwrap();
    } catch (error: any) {
      dispatch(
        showSnack({
          message: error.message || "Failed to load questions",
          severity: "error",
          open: true,
        })
      );
    }
  };
  const handleMarkAsDone = async () => {
    if (token === null) return;
    const questionUpdatePayload = questionsDataArray?.map((s) =>
      buildQuestionUpdatePayload(s)
    );

    const questionUpdateAfterSavePayload = questionsDataArray?.map(
      (s: QuestionData) => {
        return { ...s, ...buildQuestionUpdatePayload(s) };
      }
    );
    console.log(
      questionUpdateAfterSavePayload,
      questionUpdatePayload,
      "questionUpdateAfterSavePayload"
    );
    try {
      dispatch(setLoader({ open: true }));
      const response: any = await apiRequest({
        api: allApis.proofRead.post,
        payload: questionUpdatePayload,
        method: "POST",
      });
      if (response.message) {
        dispatch(
          setProofReadedData({
            questions: questionUpdateAfterSavePayload ?? [],
          })
        ); //   dispatch(setLoader({ open: false }));
        dispatch(
          showSnack({
            message: ` ${
              questionsDataArray === null ? 0 : questionsDataArray.length
            } Questions has been proofread.`,
            severity: "success",
            open: true,
          })
        );
        dispatch(setLoader({ open: false }));
        setModalInfo({
          visible: true,
          rawText: "",
          proofreadedcontent: true,
        });
      } else {
        dispatch(setLoader({ open: false }));
      }
    } catch (error) {
      console.error("Failed to mark as done:", error);
    }
  };

  // Helper function to render editable field
  const renderEditableField = (
    isEditing: boolean,
    fieldKey: any,
    originalValue: string,
    dispatchAction: any,
    coordinateField: string,
    coordinateDateField: string,
    editedDate?: string,
    label?: string,
    englishContent?: any
  ) => {
    // Get the current value from our editedContent state
    const currentValue = editedContent[fieldKey] || originalValue || "";

    return (
      <Stack spacing={"8px"} marginTop={"16px"} width="100%">
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          spacing={"16px"}
          width="100%"
          alignItems={"center"}
        >
          <Stack direction={"row"} alignItems={"center"}>
            <Typography
              variant="h6"
              fontSize="0.9rem"
              fontWeight={500}
              color="#374151"
              sx={{
                textDecoration: "underline",
                textUnderlineOffset: "4px",
              }}
            >
              {label ? label : fieldKey ?? ""}
            </Typography>
            {editedDate && (
              <Button
                sx={{ textTransform: "capitalize" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalInfo({
                    visible: true,
                    rawText: originalValue,
                  });
                }}
              >{`edited on ${editedDate}`}</Button>
            )}
          </Stack>
          {proofReadingPermissions.includes(
            proofReadingPermission.change_traslation
          ) && isEditing ? (
            <IconButton
              aria-label="edit"
              sx={{ padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();

                toggleEditing(fieldKey, false);
              }}
            >
              <Cancel />
            </IconButton>
          ) : (
            <IconButton
              aria-label="edit"
              sx={{ padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleEditing(fieldKey, true);
              }}
            >
              <Edit />
            </IconButton>
          )}
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          spacing={"16px"}
          width="100%"
          alignItems={"center"}
        >
          {" "}
          <Box width="calc(50% - 8px)" height="100%" display="flex">
            <Typography
              variant="body2"
              sx={{
                bgcolor: "#f9fafb",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                padding: "5px 10px",
                width: "100%",
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: englishContent,
                }}
              ></div>
            </Typography>
          </Box>
          <Box width="calc(50% - 8px)" height="100%" display="flex">
            {isEditing ? (
              <RichTextInput
                value={currentValue}
                onChange={(value) => {
                  handleFieldChange(
                    fieldKey,
                    value,
                    dispatchAction,
                    coordinateField,
                    coordinateDateField
                  );
                }}
                placeholder="Enter solution steps..."
                className="min-h-[100px]"
                variant="solution"
              />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  bgcolor: "#f9fafb",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  padding: "5px 10px",
                  width: "100%",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: currentValue,
                  }}
                ></div>
              </Typography>
            )}
          </Box>
        </Stack>
      </Stack>
    );
  };

  // Helper function to render editable option
  const renderEditableOption = (
    optionNumber: number,
    isEditing: boolean,
    fieldKey: keyof typeof editingStates,
    originalValue: string,
    dispatchAction: any,
    coordinateField: string,
    coordinateDateField: string,
    editedDate?: string
  ) => {
    const optionLabels = ["A", "B", "C", "D"];
    const label = optionLabels[optionNumber - 1];

    // Get the current value from our editedContent state
    const currentValue = editedContent[fieldKey] || originalValue || "";

    return (
      <ListItem disablePadding>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          spacing={"16px"}
          alignItems={"center"}
          height="100%"
          width="100%"
        >
          <ListItemText
            sx={{
              height: "100%",
              display: "flex",
              "& .MuiTypography-root": { width: "100%" },
              width: "100%",
            }}
            primary={
              isEditing ? (
                <RichTextInput
                  leftfix={`${label}.`}
                  value={currentValue}
                  onChange={(value: string) => {
                    handleFieldChange(
                      fieldKey,
                      value,
                      dispatchAction,
                      coordinateField,
                      coordinateDateField
                    );
                  }}
                  placeholder="Enter solution steps..."
                  className="min-h-[100px]"
                  variant="solution"
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    bgcolor: "#f9fafb",
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    padding: "5px 10px",
                    width: "100%",
                    // height:'100%'
                  }}
                >{`${label}. ${currentValue}`}</Typography>
              )
            }
          />
          {editedDate && (
            <Stack direction={"row"} alignItems={"center"}>
              <Button
                sx={{ textTransform: "capitalize" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalInfo({
                    visible: true,
                    rawText: originalValue,
                  });
                }}
              >{`edited on ${editedDate}`}</Button>
            </Stack>
          )}
          {proofReadingPermissions.includes(
            proofReadingPermission.change_traslation
          ) && isEditing ? (
            <IconButton
              aria-label="edit"
              sx={{ padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleEditing(fieldKey, false);
              }}
            >
              <Cancel />
            </IconButton>
          ) : (
            <IconButton
              aria-label="edit"
              sx={{ padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleEditing(fieldKey, true);
              }}
            >
              <Edit />
            </IconButton>
          )}
        </Stack>
      </ListItem>
    );
  };

  const filteredQuestionsDataArray =
    questionsDataArray === null
      ? []
      : questionsDataArray.filter(
          (questionsData) =>
            questionsData.editStatus === "pending" &&
            questionsData.questionId !== activeQuestionData.questionId
        );

  return (
    <Stack height={"100%"} width={"calc(100% - 8px)"} ml="8px">
      <Stack
        height="calc(100% - 46.99px)"
        boxShadow="rgba(0, 0, 0, 0.2) 0px 3px 3px -2px, rgba(0, 0, 0, 0.14) 0px 3px 4px 0px, rgba(0, 0, 0, 0.12) 0px 1px 8px 0px"
        bgcolor="white"
      >
        {/* Modal for showing original text */}
        <Typography variant="h6" p="15px 0 0 16px" fontWeight={600}>
          {`Question Comparison #${activeQuestionData.questionId}`}
        </Typography>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Preview" sx={{ textTransform: "capitalize" }} />
          <Tab label="Raw" sx={{ textTransform: "capitalize" }} />
        </Tabs>

        {/* Tab Content */}
        {activeTab === 1 ? (
          <Stack
            marginTop={"2px"}
            className="scrollBluePrimarythin"
            sx={{ overflowY: "auto" }}
            margin={"10px 0"}
            p="0 16px"
            spacing={"16px"}
            height={"calc(100% - 66.99px)"}
            width={"calc(100% - 32px)"}
          >
            {modalInfo.visible === true &&
              (modalInfo.proofreadedcontent ? (
                <Preview
                  onClose={() => {
                    setModalInfo({ rawText: "", visible: false });

                    handleLoadQuestiondDataArray().then(() => {
                      dispatch(setLoader({ open: false }));

                      navigate("/proofReading", { replace: true });
                      dispatch(clearActiveQuestion());
                    });
                  }}
                />
              ) : (
                <ConfirmationDialog
                  title="Confirm"
                  content={`${
                    modalInfo.rawText ? `${modalInfo.rawText} ` : ""
                  }Are you sure you want to submit all ${
                    questionsDataArray === null ? 0 : questionsDataArray.length
                  } questions, mark their translations as proofread, and proceed to the next set of questions for proofreading?`}
                  open={true}
                  noButtonText="No, Review Again"
                  yesButtonText="Yes, Submit"
                  customWidth="fit-content"
                  onClose={() => {
                    setModalInfo({ rawText: "", visible: false });
                  }}
                  onSubmit={() => {
                    handleMarkAsDone();
                    // dialogs.actions(dialogs)[0].onClick();
                    // setTimeout(() => dispatch(fetchAllExamsTabData()), 1000);
                  }}
                />
              ))}
            {/* {modalInfo.visible === true && (
            <ConfirmationDialog
              title="Confirm"
              content={`${
                modalInfo.rawText ? `${modalInfo.rawText} ` : ""
              }Are you sure you want to submit all ${
                questionsDataArray === null ? 0 : questionsDataArray.length
              } questions, mark their translations as proofread, and proceed to the next set of questions for proofreading?`}
              open={true}
              noButtonText="No, Review Again"
              yesButtonText="Yes, Submit"
              customWidth="fit-content"
              onClose={() => {
                setModalInfo({ rawText: "", visible: false });
              }}
              onSubmit={() => {
                handleMarkAsDone();
                // dialogs.actions(dialogs)[0].onClick();
                // setTimeout(() => dispatch(fetchAllExamsTabData()), 1000);
              }}
            />
          )} */}
            <Stack spacing={"8px"} marginTop={"16px"} direction={"row"}>
              {renderEditableField(
                editingStates.question,
                "question",
                activeQuestionData.questionHindi,
                changeActiveQuestionTranslation,
                activeQuestionData.editedQuestionHindiCoordinate,
                activeQuestionData.editedQuestionHindiCoordinateDate,
                activeQuestionData.editedQuestionHindiDate,
                "Question Text",
                activeQuestionData.question
              )}
            </Stack>

            <Divider />

            {/* Options English */}
            <Stack>
              <Typography
                variant="h6"
                fontSize="0.9rem"
                fontWeight={500}
                color="#374151"
                sx={{
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                }}
              >
                Options
              </Typography>
              <List>
                <Stack
                  display="flex"
                  direction={"row"}
                  justifyContent={"space-between"}
                  // alignItems={"center"}
                >
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    width="100%"
                    spacing={"16px"}
                  >
                    <Box width="calc(50% - 8px)" height="100%" display="flex">
                      <ListItem
                        disablePadding
                        sx={{
                          bgcolor: "#f9fafb",
                          borderRadius: "10px",
                          border: "1px solid #e5e7eb",
                          padding: "5px 10px",
                        }}
                      >
                        <ListItemText
                          sx={{ margin: "0" }}
                          primary={
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", gap: "5px" }}
                            >
                              A.
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: activeQuestionData.option1,
                                }}
                              ></div>
                            </Typography>
                          }
                        />
                      </ListItem>
                    </Box>
                    <Box width="calc(50% - 8px)" height="100%" display="flex">
                      {renderEditableOption(
                        1,
                        editingStates.option1,
                        "option1",
                        activeQuestionData.option1Hindi,
                        changeActiveQuestionOption1Translation,
                        activeQuestionData.editedOption1HindiCoordinate,
                        activeQuestionData.editedOption1HindiCoordinateDate,
                        activeQuestionData.editedOption1HindiDate
                      )}
                    </Box>
                  </Stack>
                </Stack>{" "}
                <Stack
                  display="flex"
                  direction={"row"}
                  justifyContent={"space-between"}
                  // alignItems={"center"}
                >
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    width="100%"
                    spacing={"16px"}
                    my="7px"
                  >
                    {" "}
                    <Box width="calc(50% - 8px)" height="100%" display="flex">
                      <ListItem
                        disablePadding
                        sx={{
                          bgcolor: "#f9fafb",
                          borderRadius: "10px",
                          border: "1px solid #e5e7eb",
                          padding: "5px 10px",
                        }}
                      >
                        <ListItemText
                          sx={{ margin: "0" }}
                          primary={
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", gap: "5px" }}
                            >
                              {" "}
                              B.
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: activeQuestionData.option2,
                                }}
                              ></div>
                            </Typography>
                          }
                        />
                      </ListItem>{" "}
                    </Box>
                    <Box width="calc(50% - 8px)" height="100%" display="flex">
                      {renderEditableOption(
                        2,
                        editingStates.option2,
                        "option2",
                        activeQuestionData.option2Hindi,
                        changeActiveQuestionOption2Translation,
                        activeQuestionData.editedOption2HindiCoordinate,
                        activeQuestionData.editedOption2HindiCoordinateDate,
                        activeQuestionData.editedOption2HindiDate
                      )}
                    </Box>
                  </Stack>
                </Stack>
                <Stack
                  display="flex"
                  direction={"row"}
                  justifyContent={"space-between"}
                  // alignItems={"center"}
                >
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    width="100%"
                    spacing={"16px"}
                    mb="7px"
                  >
                    {" "}
                    <Box width="calc(50% - 8px)" height="100%" display="flex">
                      <ListItem
                        disablePadding
                        sx={{
                          bgcolor: "#f9fafb",
                          borderRadius: "10px",
                          border: "1px solid #e5e7eb",
                          padding: "5px 10px",
                        }}
                      >
                        <ListItemText
                          sx={{ margin: "0" }}
                          primary={
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", gap: "5px" }}
                            >
                              {" "}
                              C.
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: activeQuestionData.option3,
                                }}
                              ></div>
                            </Typography>
                          }
                        />
                      </ListItem>{" "}
                    </Box>
                    <Box width="calc(50% - 8px)" height="100%" display="flex">
                      {renderEditableOption(
                        3,
                        editingStates.option3,
                        "option3",
                        activeQuestionData.option3Hindi,
                        changeActiveQuestionOption3Translation,
                        activeQuestionData.editedOption3HindiCoordinate,
                        activeQuestionData.editedOption3HindiCoordinateDate,
                        activeQuestionData.editedOption3HindiDate
                      )}{" "}
                    </Box>
                  </Stack>
                </Stack>
                <Stack
                  display="flex"
                  direction={"row"}
                  justifyContent={"space-between"}
                  // alignItems={"center"}
                >
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    width="100%"
                    spacing={"16px"}
                  >
                    <Box width="calc(50% - 8px)" height="100%" display="flex">
                      <ListItem
                        disablePadding
                        sx={{
                          bgcolor: "#f9fafb",
                          borderRadius: "10px",
                          border: "1px solid #e5e7eb",
                          padding: "5px 10px",
                        }}
                      >
                        <ListItemText
                          sx={{ margin: "0" }}
                          primary={
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", gap: "5px" }}
                            >
                              {" "}
                              D.
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: activeQuestionData.option4,
                                }}
                              ></div>
                            </Typography>
                          }
                        />
                      </ListItem>
                    </Box>{" "}
                    <Box width="calc(50% - 8px)" height="100%" display="flex">
                      {renderEditableOption(
                        4,
                        editingStates.option4,
                        "option4",
                        activeQuestionData.option4Hindi,
                        changeActiveQuestionOption4Translation,
                        activeQuestionData.editedOption4HindiCoordinate,
                        activeQuestionData.editedOption4HindiCoordinateDate,
                        activeQuestionData.editedOption4HindiDate
                      )}
                    </Box>
                  </Stack>
                </Stack>
              </List>
            </Stack>

            <Divider />

            {/* Explanation - Editable */}
            {renderEditableField(
              editingStates.explanation,
              "explanation",
              activeQuestionData.explanationHindi,
              changeActiveQuestionExplanationTranslation,
              activeQuestionData.editedExplanationHindiCoordinate,
              activeQuestionData.editedExplanationHindiCoordinateDate,
              activeQuestionData.editedExplanationHindiDate,
              "Explanation",
              activeQuestionData.explanation
            )}
          </Stack>
        ) : (
          // Preview Tab - Side by side comparison
          <Stack
            className="scrollBluePrimarythin"
            sx={{ overflowY: "auto" }}
            height={"calc(100% - 66.99px)"}
            p={3}
            alignItems={"center"}
          >
            <Stack
              direction="row"
              gap={3}
              width="100%"
              justifyContent={"center"}
              flexWrap={"wrap"}
            >
              {/* English Preview */}
              <QuestionPreviewItem
                questionData={{
                  questionId: activeQuestionData.questionId,
                  question: activeQuestionData.question,
                  questionHindi: activeQuestionData.questionHindi,
                  option1: activeQuestionData.option1,
                  option2: activeQuestionData.option2,
                  option3: activeQuestionData.option3,
                  option4: activeQuestionData.option4,
                  option1Hindi: activeQuestionData.option1Hindi,
                  option2Hindi: activeQuestionData.option2Hindi,
                  option3Hindi: activeQuestionData.option3Hindi,
                  option4Hindi: activeQuestionData.option4Hindi,
                  explanation: activeQuestionData.explanation,
                  explanationHindi: activeQuestionData.explanationHindi,
                  correctAnswerEnglish: activeQuestionData.correctAnswerEnglish,
                  correctAnswerHindi: activeQuestionData.correctAnswerHindi,
                }}
                language="english"
              />

              {/* Hindi Preview */}
              {activeQuestionData.questionHindi ||
              activeQuestionData.option1Hindi ||
              activeQuestionData.option2Hindi ||
              activeQuestionData.option3Hindi ||
              activeQuestionData.option4Hindi ||
              activeQuestionData.explanationHindi ? (
                <QuestionPreviewItem
                  questionData={{
                    questionId: activeQuestionData.questionId,
                    question: activeQuestionData.question,
                    questionHindi: activeQuestionData.questionHindi,
                    option1: activeQuestionData.option1,
                    option2: activeQuestionData.option2,
                    option3: activeQuestionData.option3,
                    option4: activeQuestionData.option4,
                    option1Hindi: activeQuestionData.option1Hindi,
                    option2Hindi: activeQuestionData.option2Hindi,
                    option3Hindi: activeQuestionData.option3Hindi,
                    option4Hindi: activeQuestionData.option4Hindi,
                    explanation: activeQuestionData.explanation,
                    explanationHindi: activeQuestionData.explanationHindi,
                    correctAnswerEnglish:
                      activeQuestionData.correctAnswerEnglish,
                    correctAnswerHindi: activeQuestionData.correctAnswerHindi,
                  }}
                  language="hindi"
                />
              ) : (
                <Stack
                  sx={{
                    width: "375px",
                    minWidth: "375px",
                    padding: "16px",
                    border: "1px solid #FFB74D",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    bgcolor: "#FFF3E0",
                    height: "fit-content",
                  }}
                >
                  <Typography
                    color={"#F57C00"}
                    fontWeight={600}
                    fontSize={"16px"}
                    fontFamily={"Gilroy"}
                    textAlign={"center"}
                  >
                    No Hindi translation available
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        )}
        {/* Action Buttons */}
      </Stack>
      {questionsDataArray &&
      questionsDataArray.length > 0 &&
      questionsDataArray[questionsDataArray.length - 1].questionId ===
        activeQuestionData.questionId ? (
        <Stack
          my="5px"
          direction={"row"}
          justifyContent={"flex-end"}
          spacing={"8px"}
        >
          <Button
            sx={{ textTransform: "capitalize" }}
            variant="outlined"
            disabled={Object.values(editingStates).some((state) => state)}
            onClick={(e) => {
              e.stopPropagation();
              if (filteredQuestionsDataArray.length > 0) {
                dispatch(
                  goToNextQustion({
                    activeQuestionRowNumber: activeQuestionData.questionId,
                    corrections: null,
                    nextId: filteredQuestionsDataArray[0].questionId,
                  })
                );
              } else {
                setModalInfo({
                  visible: true,
                  rawText: "All the Questions have been reviewed.",
                });
              }
              // handleSaveQuestionUpdates();
            }}
          >
            Next – No Changes
          </Button>
          <Button
            variant="contained"
            disabled={!Object.values(editingStates).some((state) => state)}
            sx={{ textTransform: "capitalize" }}
            onClick={(e) => {
              e.stopPropagation();
              // handleMarkAsDone();
              if (filteredQuestionsDataArray.length > 0) {
                dispatch(
                  showSnack({
                    message: `${filteredQuestionsDataArray.length} Question${
                      filteredQuestionsDataArray.length > 1 ? "s are" : "is"
                    } pending to e Reviewd`,
                    severity: "error",
                    open: true,
                  })
                );
                dispatch(
                  goToNextQustion({
                    activeQuestionRowNumber: activeQuestionData.questionId,
                    corrections: Object.fromEntries(
                      Object.entries(editedContent).filter(
                        ([key]) => editingStates[key]
                      )
                    ),
                    nextId: filteredQuestionsDataArray[0].questionId,
                  })
                );
              } else {
                setModalInfo({
                  visible: true,
                  rawText: "",
                });
              }
            }}
          >
            Submit
          </Button>
        </Stack>
      ) : (
        <Stack
          my="5px"
          direction={"row"}
          justifyContent={"flex-end"}
          spacing={"8px"}
        >
          <Button
            sx={{ textTransform: "capitalize" }}
            variant="outlined"
            disabled={Object.values(editingStates).some((state) => state)}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(
                goToNextQustion({
                  activeQuestionRowNumber: activeQuestionData.questionId,
                  corrections: Object.fromEntries(
                    Object.entries(editedContent).filter(
                      ([key]) => editingStates[key]
                    )
                  ),
                })
              );
              // handleSaveQuestionUpdates();
            }}
          >
            Next – No Changes
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: "capitalize" }}
            disabled={!Object.values(editingStates).some((state) => state)}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(
                goToNextQustion({
                  activeQuestionRowNumber: activeQuestionData.questionId,
                  corrections: Object.fromEntries(
                    Object.entries(editedContent).filter(
                      ([key]) => editingStates[key]
                    )
                  ),
                })
              );
            }}
          >
            Save Correction & Next
          </Button>
          {filteredQuestionsDataArray.length === 0 ? (
            <Button
              variant="contained"
              // disabled={!Object.values(editingStates).some((state) => state)}
              sx={{ textTransform: "capitalize" }}
              onClick={(e) => {
                e.stopPropagation();
                // handleMarkAsDone();
                if (filteredQuestionsDataArray.length > 0) {
                  dispatch(
                    showSnack({
                      message: `${filteredQuestionsDataArray.length} Question${
                        filteredQuestionsDataArray.length > 1 ? "s are" : "is"
                      } pending to e Reviewd`,
                      severity: "error",
                      open: true,
                    })
                  );
                  dispatch(
                    goToNextQustion({
                      activeQuestionRowNumber: activeQuestionData.questionId,
                      corrections: Object.fromEntries(
                        Object.entries(editedContent).filter(
                          ([key]) => editingStates[key]
                        )
                      ),
                      nextId: filteredQuestionsDataArray[0].questionId,
                    })
                  );
                } else {
                  setModalInfo({
                    visible: true,
                    rawText: "",
                  });
                }
              }}
            >
              Submit
            </Button>
          ) : (
            ""
          )}
        </Stack>
      )}
    </Stack>
  );
};
